// src/components/ErrorBoundary.tsx
import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-40 flex items-center justify-center">
          <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-6 rounded-[var(--radius-lg)] max-w-md">
            <h2 className="text-xl font-heading font-semibold text-[var(--color-danger)] mb-4">
              Oops! Something went wrong
            </h2>
            <p className="text-[var(--color-text-secondary)] mb-4">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
            <button
              onClick={this.handleReset}
              className="bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-text-inverse)] font-medium py-2 px-4 rounded-[var(--radius-sm)]"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
